import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  HiArrowLeft,
  HiCheckCircle,
  HiEye,
  HiEyeOff,
  HiLockClosed,
} from "react-icons/hi";
import { Link } from "react-router-dom";

import { ResetPasswordData } from "@/types/auth";

interface NewPasswordFormProps {
  email: string;
  error?: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  onBack: () => void;
  onSubmit: (data: ResetPasswordData) => void;
  otp: string;
}

export default function NewPasswordForm({
  email,
  error,
  isLoading = false,
  isSuccess = false,
  onBack,
  onSubmit,
  otp,
}: NewPasswordFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<ResetPasswordData>({
    defaultValues: { email, otp },
  });

  const password = watch("password");

  const handleFormSubmit = (data: ResetPasswordData) => {
    onSubmit(data);
  };

  // Password strength validation
  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = password ? getPasswordStrength(password) : 0;
  const strengthColors = [
    "",
    "text-error",
    "text-warning",
    "text-info",
    "text-success",
    "text-success",
  ];
  const strengthLabels = ["", "Very Weak", "Weak", "Fair", "Good", "Strong"];

  if (isSuccess) {
    return (
      <div className="card w-full max-w-md bg-base-100 shadow-xl">
        <div className="card-body text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success">
              <HiCheckCircle className="h-10 w-10 text-success-content" />
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold text-base-content">
            Password Reset Successful
          </h2>
          <p className="mb-6 text-base-content/70">
            Your password has been successfully reset. You can now sign in with
            your new password.
          </p>
          <Link className="btn btn-primary w-full" to="/auth/login">
            Continue to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
              <HiLockClosed className="h-10 w-10 text-primary-content" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content">
            Create New Password
          </h2>
          <p className="mt-2 text-base-content/70">
            Enter a strong password for your account
          </p>
          <p className="mt-1 text-sm text-base-content/50">for {email}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Hidden fields */}
          <input {...register("email")} type="hidden" />
          <input {...register("otp")} type="hidden" />

          {/* Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">New Password</span>
            </label>
            <div className="relative">
              <input
                {...register("password", {
                  minLength: {
                    message: "Password must be at least 8 characters",
                    value: 8,
                  },
                  required: "Password is required",
                  validate: {
                    hasLowercase: (value) =>
                      /[a-z]/.test(value) ||
                      "Password must contain at least one lowercase letter",
                    hasNumber: (value) =>
                      /\d/.test(value) ||
                      "Password must contain at least one number",
                    hasSpecialChar: (value) =>
                      /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                      "Password must contain at least one special character",
                    hasUppercase: (value) =>
                      /[A-Z]/.test(value) ||
                      "Password must contain at least one uppercase letter",
                  },
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.password
                    ? "input-error"
                    : passwordStrength >= 4
                      ? "input-success"
                      : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your new password"
                type={showPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowPassword(!showPassword)}
                type="button"
              >
                {showPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs text-base-content/60">
                    Password Strength:
                  </span>
                  <span
                    className={`text-xs font-semibold ${strengthColors[passwordStrength]}`}
                  >
                    {strengthLabels[passwordStrength]}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-base-300">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength >= 4
                        ? "bg-success"
                        : passwordStrength >= 3
                          ? "bg-info"
                          : passwordStrength >= 2
                            ? "bg-warning"
                            : "bg-error"
                    }`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {errors.password && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.password.message}
                </span>
              </label>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Confirm New Password</span>
            </label>
            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.confirmPassword ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Confirm your new password"
                type={showConfirmPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                type="button"
              >
                {showConfirmPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.confirmPassword.message}
                </span>
              </label>
            )}
          </div>

          {/* Password Requirements */}
          <div className="rounded-lg bg-base-200 p-4">
            <h4 className="mb-3 text-sm font-semibold">
              Password Requirements:
            </h4>
            <div className="grid grid-cols-1 gap-2 text-xs">
              <div
                className={`flex items-center ${password?.length >= 8 ? "text-success" : "text-base-content/60"}`}
              >
                <HiCheckCircle
                  className={`mr-2 h-3 w-3 ${password?.length >= 8 ? "text-success" : "text-base-content/30"}`}
                />
                At least 8 characters long
              </div>
              <div
                className={`flex items-center ${/[A-Z]/.test(password || "") ? "text-success" : "text-base-content/60"}`}
              >
                <HiCheckCircle
                  className={`mr-2 h-3 w-3 ${/[A-Z]/.test(password || "") ? "text-success" : "text-base-content/30"}`}
                />
                Contains uppercase letter (A-Z)
              </div>
              <div
                className={`flex items-center ${/[a-z]/.test(password || "") ? "text-success" : "text-base-content/60"}`}
              >
                <HiCheckCircle
                  className={`mr-2 h-3 w-3 ${/[a-z]/.test(password || "") ? "text-success" : "text-base-content/30"}`}
                />
                Contains lowercase letter (a-z)
              </div>
              <div
                className={`flex items-center ${/\d/.test(password || "") ? "text-success" : "text-base-content/60"}`}
              >
                <HiCheckCircle
                  className={`mr-2 h-3 w-3 ${/\d/.test(password || "") ? "text-success" : "text-base-content/30"}`}
                />
                Contains at least one number (0-9)
              </div>
              <div
                className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(password || "") ? "text-success" : "text-base-content/60"}`}
              >
                <HiCheckCircle
                  className={`mr-2 h-3 w-3 ${/[!@#$%^&*(),.?":{}|<>]/.test(password || "") ? "text-success" : "text-base-content/30"}`}
                />
                Contains special character (!@#$%^&*)
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={isLoading || passwordStrength < 4}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Updating Password...
              </>
            ) : (
              <>
                <HiLockClosed className="mr-2 h-4 w-4" />
                Update Password
              </>
            )}
          </button>
        </form>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button className="btn btn-ghost btn-sm normal-case" onClick={onBack}>
            <HiArrowLeft className="mr-2 h-4 w-4" />
            Back to OTP
          </button>
        </div>

        {/* Security Info */}
        <div className="mt-4 rounded-lg bg-base-200 p-4">
          <h4 className="mb-2 text-sm font-semibold">Security Tips:</h4>
          <ul className="space-y-1 text-xs text-base-content/70">
            <li>• Use a unique password you have not used before</li>
            <li>• Avoid common words or personal information</li>
            <li>• Consider using a password manager</li>
            <li>• Do not share your password with anyone</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
